import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  loadAssessment,
  submitAssessmentStep,
  updateAssessmentStatus,
} from "../api/client";
import LiteGuideAssessment from "../components/LiteGuideAssessment";
import SurveyFormAssessment from "../components/SurveyFormAssessment";
import { partAnswerKey } from "../utils/assessmentKeys";
import { hydrateAssessmentFromResponse } from "../utils/assessmentLoad";
import {
  countQuestionsInSteps,
  countRemainingFullQuestions,
} from "../utils/assessmentIntroMeta";
import {
  ASSESSMENT_UX_FORM,
  readAssessmentUxMode,
  writeAssessmentUxMode,
} from "../utils/assessmentUxMode";
import { partitionStepQuestions } from "../utils/liteQuestions";
import {
  normalizeParticipantAssessmentType,
  trackLabel,
  upgradeOfferTitle,
} from "../utils/participantAssessmentType";
import { brandColor } from "../utils/participantBranding";

function validateQuestions(questions, answers, toastError) {
  for (const q of questions) {
    const qtype = (q.type || "mcq").toLowerCase();
    for (const p of q.parts || []) {
      const key = partAnswerKey(q, p);
      const val = answers[key] ?? p.answer ?? "";

      if (qtype === "mcq" || !q.type) {
        if (p.type !== "importance" && p.type !== "agreement") continue;
        if (!val || !String(val).trim()) {
          toastError("Please answer all questions before continuing.");
          return false;
        }
      }

      if (qtype === "text") {
        const part = q.parts?.[0] || { type: "text" };
        const ak = partAnswerKey(q, part);
        const v = answers[ak] ?? part.answer ?? "";
        const s = String(v).trim();
        if (!s || (s.length < 2 && s.toUpperCase() !== "NA")) {
          toastError(
            "Please complete all text responses (at least 2 characters, or NA)."
          );
          return false;
        }
        continue;
      }

      if (qtype === "dropdown") {
        if (!val || !String(val).trim()) {
          toastError("Please select an option for each dropdown.");
          return false;
        }
      }
    }
  }
  return true;
}

function readInitialAssessmentState(initialLoadResponse, assessmentTrack) {
  if (!initialLoadResponse) return null;
  return hydrateAssessmentFromResponse(initialLoadResponse, { assessmentTrack });
}

export default function AssessmentPage({
  surveyToken,
  assessmentTrack = "full",
  initialLoadResponse = null,
  assessmentType: assessmentTypeProp,
  onLiteCompleted,
  onFullCompleted,
}) {
  const initial = readInitialAssessmentState(initialLoadResponse, assessmentTrack);
  const [loading, setLoading] = useState(!initial || initial.earlyExit);
  const [steps, setSteps] = useState(initial?.steps ?? []);
  const [userData, setUserData] = useState(initial?.userData ?? null);
  const [track, setTrack] = useState(initial?.track ?? assessmentTrack);
  const [liteQuestionIds, setLiteQuestionIds] = useState(
    initial?.liteQuestionIds ?? []
  );
  const [currentStep, setCurrentStep] = useState(initial?.currentStep ?? 1);
  const [answers, setAnswers] = useState(initial?.answers ?? {});
  const [saving, setSaving] = useState(false);
  const [uxMode, setUxMode] = useState(() => readAssessmentUxMode());
  const [assessmentType, setAssessmentType] = useState(() =>
    normalizeParticipantAssessmentType(
      assessmentTypeProp || initialLoadResponse?.data?.assessment_type
    )
  );

  const isLiteSinglePage = track === "lite";
  const isFullContinuation =
    track === "full" && liteQuestionIds.length > 0;
  const isFormMode = uxMode === ASSESSMENT_UX_FORM;

  const handleUxModeChange = useCallback((next) => {
    setUxMode(writeAssessmentUxMode(next));
  }, []);

  const applyLoadedResponse = useCallback(
    (res) => {
      const hydrated = hydrateAssessmentFromResponse(res, {
        assessmentTrack,
        onLiteCompleted,
        onFullCompleted,
      });
      if (hydrated.earlyExit) return true;
      setSteps(hydrated.steps);
      setUserData(hydrated.userData);
      setTrack(hydrated.track);
      setLiteQuestionIds(hydrated.liteQuestionIds);
      setCurrentStep(hydrated.currentStep);
      setAnswers(hydrated.answers);
      setAssessmentType(
        normalizeParticipantAssessmentType(res?.data?.assessment_type)
      );
      return false;
    },
    [assessmentTrack, onLiteCompleted, onFullCompleted]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await loadAssessment(surveyToken);
      applyLoadedResponse(res);
    } catch (e) {
      toast.error(e.message || "Could not load assessment");
    } finally {
      setLoading(false);
    }
  }, [surveyToken, applyLoadedResponse]);

  useEffect(() => {
    if (initialLoadResponse) {
      const hydrated = hydrateAssessmentFromResponse(initialLoadResponse, {
        assessmentTrack,
        onLiteCompleted,
        onFullCompleted,
      });
      if (!hydrated.earlyExit) {
        setLoading(false);
      }
      return;
    }
    fetchData();
  }, [
    surveyToken,
    initialLoadResponse,
    assessmentTrack,
    onLiteCompleted,
    onFullCompleted,
    fetchData,
  ]);

  const stepData = steps[currentStep - 1];

  const { liteQuestions, remainingQuestions } = useMemo(
    () =>
      partitionStepQuestions(
        stepData?.questions,
        isFullContinuation ? liteQuestionIds : []
      ),
    [stepData, isFullContinuation, liteQuestionIds]
  );

  const hasLiteOnStep = isFullContinuation && liteQuestions.length > 0;
  const hasRemainingOnStep = remainingQuestions.length > 0;

  const questionsForCurrentStep = useMemo(() => {
    if (!stepData) return [];
    if (isFullContinuation && hasRemainingOnStep) return remainingQuestions;
    if (hasRemainingOnStep) return remainingQuestions;
    return stepData.questions || [];
  }, [stepData, isFullContinuation, hasRemainingOnStep, remainingQuestions]);

  const remainingFullCount = useMemo(
    () =>
      isFullContinuation
        ? countRemainingFullQuestions(steps, liteQuestionIds)
        : countQuestionsInSteps(steps),
    [isFullContinuation, steps, liteQuestionIds]
  );

  const companyName = userData?.company?.company_name;
  const companyLogo = userData?.company?.company_logo;
  const branding = initialLoadResponse?.data?.branding || {};
  const productLogoUrl = branding.logo_url || null;
  const productDisplayName =
    branding.product_display_name ||
    (!assessmentType?.uses_five_x_branding ? companyName : null) ||
    null;
  const primaryColor = brandColor(branding);

  const setAnswerField = (question, part, value) => {
    const key = partAnswerKey(question, part);
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayloadForStep = (step) => {
    const questions = (step?.questions || []).map((q) => ({
      id: q.id,
      text: q.text,
      parts: (q.parts || []).map((p) => {
        const key = partAnswerKey(q, p);
        return {
          type: p.type,
          options: p.options,
          answer: answers[key] ?? p.answer ?? "",
        };
      }),
    }));
    return {
      step: step.step,
      title: step.title || `Section ${step.step}`,
      questions,
    };
  };

  const saveStep = async (stepToSave) => {
    const step = stepToSave ?? stepData;
    if (!step) return false;
    setSaving(true);
    try {
      await submitAssessmentStep(
        surveyToken,
        step.step,
        buildPayloadForStep(step)
      );
      return true;
    } catch (e) {
      toast.error(e.message || "Could not save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validateStep = (stepToValidate) => {
    const step = stepToValidate ?? stepData;
    if (!step) return false;
    const toValidate =
      isFullContinuation && step === stepData && hasRemainingOnStep
        ? remainingQuestions
        : step.questions || [];
    return validateQuestions(toValidate, answers, (msg) => toast.error(msg));
  };

  const validateAllLiteSteps = () => {
    for (const step of steps) {
      if (!validateQuestions(step.questions, answers, (msg) => toast.error(msg))) {
        return false;
      }
    }
    return true;
  };

  const handleLiteComplete = async () => {
    if (!validateAllLiteSteps()) return;
    setSaving(true);
    try {
      for (const step of steps) {
        await submitAssessmentStep(
          surveyToken,
          step.step,
          buildPayloadForStep(step)
        );
      }
      await updateAssessmentStatus(surveyToken, "LiteCompleted");
      toast.success(`${upgradeOfferTitle(assessmentType)}.`);
      onLiteCompleted?.();
    } catch (e) {
      toast.error(e.message || "Could not save assessment");
    } finally {
      setSaving(false);
    }
  };

  const handleGuideStepComplete = useCallback(async () => {
    if (!validateStep()) return { finished: false };
    const ok = await saveStep();
    if (!ok) return { finished: false };
    if (currentStep >= steps.length) {
      try {
        await updateAssessmentStatus(surveyToken, "Completed");
        toast.success("Assessment completed. Thank you!");
        onFullCompleted?.();
      } catch (e) {
        toast.error(e.message || "Could not complete assessment");
        return { finished: false };
      }
      return { finished: true };
    }
    setCurrentStep((s) => s + 1);
    return { finished: false };
  }, [currentStep, steps.length, surveyToken, onFullCompleted, stepData, answers, isFullContinuation, hasRemainingOnStep, remainingQuestions]);

  const handleFullFormComplete = async () => {
    if (!validateStep()) return;
    const ok = await saveStep();
    if (!ok) return;
    try {
      await updateAssessmentStatus(surveyToken, "Completed");
      toast.success("Assessment completed. Thank you!");
      onFullCompleted?.();
    } catch (e) {
      toast.error(e.message || "Could not complete assessment");
    }
  };

  const handleFullFormNext = async () => {
    if (!validateStep()) return;
    const ok = await saveStep();
    if (!ok) return;
    if (currentStep >= steps.length) {
      await handleFullFormComplete();
      return;
    }
    setCurrentStep((s) => s + 1);
  };

  /* Skip pillars with no remaining questions (e.g. after lite continuation) */
  useEffect(() => {
    if (isLiteSinglePage || loading || !stepData) return;
    if (questionsForCurrentStep.length > 0) return;
    if (currentStep >= steps.length) return;
    if (!hasLiteOnStep) return;

    let cancelled = false;
    (async () => {
      const ok = await saveStep(stepData);
      if (!cancelled && ok) setCurrentStep((s) => s + 1);
    })();
    return () => { cancelled = true; };
  }, [
    isLiteSinglePage,
    loading,
    currentStep,
    steps.length,
    stepData,
    questionsForCurrentStep.length,
    hasLiteOnStep,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]">
        <p className="text-[#004F73] font-medium">Loading assessment…</p>
      </div>
    );
  }

  if (!steps.length) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#F9F9F9]">
        <p className="text-red-600 text-center">Assessment data is not available.</p>
      </div>
    );
  }

  if (isLiteSinglePage) {
    if (isFormMode) {
      return (
        <>
          <Toaster position="top-center" />
          <SurveyFormAssessment
            steps={steps}
            currentStep={1}
            answers={answers}
            setAnswerField={setAnswerField}
            companyName={companyName}
            companyLogo={companyLogo}
            trackBanner={trackLabel(assessmentType, "lite")}
            singlePageMode
            saving={saving}
            onComplete={handleLiteComplete}
            uxMode={uxMode}
            onUxModeChange={handleUxModeChange}
            surveyToken={surveyToken}
            assessmentTrack="lite"
            assessmentType={assessmentType}
            productLogoUrl={productLogoUrl}
            productDisplayName={productDisplayName}
            primaryColor={primaryColor}
          />
        </>
      );
    }
    return (
      <>
        <Toaster position="top-center" />
        <LiteGuideAssessment
          surveyToken={surveyToken}
          steps={steps}
          assessmentTrack="lite"
          companyName={companyName}
          companyLogo={companyLogo}
          answers={answers}
          setAnswerField={setAnswerField}
          onComplete={handleLiteComplete}
          saving={saving}
          uxMode={uxMode}
          onUxModeChange={handleUxModeChange}
          assessmentType={assessmentType}
          productLogoUrl={productLogoUrl}
          productDisplayName={productDisplayName}
          primaryColor={primaryColor}
        />
      </>
    );
  }

  if (!stepData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#F9F9F9]">
        <p className="text-red-600 text-center">Assessment data is not available.</p>
      </div>
    );
  }

  if (questionsForCurrentStep.length === 0 && currentStep < steps.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]">
        <p className="text-[#004F73] font-medium">Loading next pillar…</p>
      </div>
    );
  }

  const trackBanner = isFullContinuation
    ? `${trackLabel(assessmentType, "full")} — ${remainingFullCount} remaining question${
        remainingFullCount === 1 ? "" : "s"
      }`
    : null;

  if (isFormMode) {
    return (
      <>
        <Toaster position="top-center" />
        <SurveyFormAssessment
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          answers={answers}
          setAnswerField={setAnswerField}
          companyName={companyName}
          companyLogo={companyLogo}
          trackBanner={trackBanner}
          singlePageMode={false}
          questionsOverride={
            isFullContinuation ? questionsForCurrentStep : null
          }
          saving={saving}
          onComplete={handleFullFormComplete}
          onNextStep={handleFullFormNext}
          uxMode={uxMode}
          onUxModeChange={handleUxModeChange}
          surveyToken={surveyToken}
          assessmentTrack="full"
          assessmentType={assessmentType}
          productLogoUrl={productLogoUrl}
          productDisplayName={productDisplayName}
          primaryColor={primaryColor}
        />
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <LiteGuideAssessment
        key={currentStep}
        surveyToken={surveyToken}
        steps={steps}
        questions={questionsForCurrentStep}
        assessmentTrack="full"
        currentStep={currentStep}
        totalSteps={steps.length}
        skipLanding={currentStep > 1}
        trackBanner={trackBanner}
        landingQuestionCount={remainingFullCount}
        liteQuestionIds={isFullContinuation ? liteQuestionIds : null}
        companyName={companyName}
        companyLogo={companyLogo}
        answers={answers}
        setAnswerField={setAnswerField}
        onStepComplete={handleGuideStepComplete}
        onComplete={() => onFullCompleted?.()}
        saving={saving}
        uxMode={uxMode}
        onUxModeChange={handleUxModeChange}
        assessmentType={assessmentType}
        productLogoUrl={productLogoUrl}
        productDisplayName={productDisplayName}
        primaryColor={primaryColor}
      />
    </>
  );
}
